import { useCallback, useState, type PropsWithChildren } from 'react';
import type React from 'react';

import type { VariantContext } from './actors/types';
import { StartAPetitionPageStateContextProvider } from './context';
import { useStartAPetitionState } from './hooks';

type CurrentDraft = VariantContext['draft'];

type StartAPetitionPageStateProviderProps = PropsWithChildren<{
	currentDraft?: CurrentDraft;
	onRemount: (draft: CurrentDraft) => void;
}>;

const StartAPetitionPageStateProviderInner: React.FC<StartAPetitionPageStateProviderProps> = ({
	children,
	currentDraft,
	onRemount,
}) => {
	const { actions, data } = useStartAPetitionState({ currentDraft, onRemount });

	return (
		<StartAPetitionPageStateContextProvider actions={actions} data={data}>
			{children}
		</StartAPetitionPageStateContextProvider>
	);
};

export const StartAPetitionPageStateProvider: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	/** Used to force remount the provider when the draft changes */
	const [key, setKey] = useState(0);
	/** Holds the current draft to be passed to the provider on remount */
	const [currentDraft, setCurrentDraft] = useState<CurrentDraft>(undefined);

	/**
	 * Handles remounting the provider with the updated draft when the user navigates using the browser's back/forward buttons
	 * This is necessary to synchronize the state machine with the URL and ensure the correct draft is loaded based on the URL
	 * without this, the state machine would not re-initialize with the correct draft when navigating back to a previous step, causing potential mismatches between the URL and the loaded draft
	 */
	const onRemount = useCallback((draft: CurrentDraft) => {
		setCurrentDraft(draft);
		setKey((prevKey) => prevKey + 1);
	}, []);

	return (
		<StartAPetitionPageStateProviderInner key={key} currentDraft={currentDraft} onRemount={onRemount}>
			{children}
		</StartAPetitionPageStateProviderInner>
	);
};
